/**
 * CommunityDownloadsSection — public-website section that lists the
 * admin-curated OnBuzz Community releases with download buttons for
 * each GitHub asset.
 *
 * Sibling of DownloadsSection (the Enterprise downloads). The two are
 * intentionally separate components rather than one with a "mode" prop,
 * because:
 *   - Enterprise artifacts are URL-derived from a version number
 *     (we know the file-name pattern + the host)
 *   - Community artifacts are fetched live from GitHub (the file names
 *     can differ release-to-release and are authored at tag time)
 *
 * Use this on any brand build where OnBuzz Community should be visible.
 * Add it next to DownloadsSection on the home/downloads route.
 */
import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Chip,
  Typography,
  Button,
  Skeleton,
  Stack,
  Link,
  Tooltip,
  Collapse,
} from '@mui/material';
import {
  OpenInNew as OpenInNewIcon,
  Download as DownloadIcon,
  GitHub as GitHubIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
} from '@mui/icons-material';
import {
  fetchAllCommunityReleaseDetails,
  type CommunityReleaseDetail,
  type CommunityReleaseAsset,
} from '../api/communityDownloadsApi';
import {
  detectPlatform,
  organizeAssets,
  classifyAsset,
  type DetectedEnv,
  type Platform,
} from '../utils/platformDetection';

const GITHUB_REPO = 'Loxia-ai/onbuzz-community';

const PLATFORM_LABEL: Record<Platform, string> = {
  windows: 'Windows',
  macos: 'macOS',
  linux: 'Linux',
  unknown: 'your platform',
};

function formatBytes(n: number): string {
  if (!n || n <= 0) return '';
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  if (n < 1024 * 1024 * 1024) return `${(n / (1024 * 1024)).toFixed(1)} MB`;
  return `${(n / (1024 * 1024 * 1024)).toFixed(2)} GB`;
}

function channelColor(channel: 'stable' | 'experimental') {
  return channel === 'stable' ? 'success' : 'warning';
}

/**
 * Compact secondary download button — name + size, outlined, full-width.
 * Optional descriptor (e.g. "Windows · installer") appears as a faint
 * caption above the filename so the user understands what they're
 * looking at without parsing the raw artifact name.
 */
function AssetRowButton({
  asset,
  descriptor,
}: {
  asset: CommunityReleaseAsset;
  descriptor?: string;
}) {
  return (
    <Tooltip title={`Download ${asset.name}`}>
      <Button
        variant="outlined"
        size="small"
        href={asset.downloadUrl}
        rel="noopener noreferrer"
        startIcon={<DownloadIcon />}
        sx={{
          justifyContent: 'flex-start',
          textTransform: 'none',
          fontSize: '0.8125rem',
          py: 0.75,
        }}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', width: '100%', minWidth: 0 }}>
          {descriptor && (
            <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.2, fontSize: '0.7rem' }}>
              {descriptor}
            </Typography>
          )}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%', minWidth: 0 }}>
            <Box
              sx={{
                // `flex: 1` alone doesn't let this Box shrink below its
                // content's intrinsic width — flex items default to
                // `min-width: auto`. Forcing `minWidth: 0` here is what
                // actually lets the ellipsis kick in for long filenames.
                flex: '1 1 0',
                minWidth: 0,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                textAlign: 'left',
              }}
            >
              {asset.name}
            </Box>
            {asset.size > 0 && (
              <Typography variant="caption" color="text.secondary" sx={{ ml: 'auto' }}>
                {formatBytes(asset.size)}
              </Typography>
            )}
          </Box>
        </Box>
      </Button>
    </Tooltip>
  );
}

/** Plain-English label for an asset, e.g. "Windows · Installer". */
function assetDescriptor(asset: CommunityReleaseAsset): string {
  const t = classifyAsset(asset.name);
  const plat =
    t.platform === 'windows' ? 'Windows' :
    t.platform === 'macos'   ? 'macOS' :
    t.platform === 'linux'   ? 'Linux' :
    '';
  const kindLabel =
    t.kind === 'desktop-app'   ? 'Desktop app' :
    t.kind === 'cli-installer' ? 'Installer' :
    t.kind === 'cli-portable'  ? 'Portable (terminal)' :
    '';
  return [plat, kindLabel].filter(Boolean).join(' · ');
}

interface ReleaseCardProps {
  detail: CommunityReleaseDetail;
  detected: DetectedEnv;
}

function ReleaseCard({ detail, detected }: ReleaseCardProps) {
  const assets = detail.assets || [];
  const hasAssets = assets.length > 0;
  const [showAdvanced, setShowAdvanced] = useState(false);

  // Group assets into:
  //   • primary           — the big "Download for <OS>" button (desktop app
  //                          for visitor's platform, or CLI installer fallback)
  //   • otherPlatformDesktopApps — desktop apps for the other 2 OSes
  //   • cliAssets         — installers + portable for all platforms
  //                         (collapsible "command-line tool — advanced")
  //   • uncategorized     — anything we can't place
  // Metadata files (latest.yml, *.blockmap, *.sha512 …) are dropped
  // entirely inside organizeAssets — never shown to the user.
  const organized = useMemo(
    () => organizeAssets(assets, detected, a => a.name),
    [assets, detected],
  );
  const { primary, otherPlatformDesktopApps, cliAssets, uncategorized } = organized;
  const primaryDescriptor = primary ? assetDescriptor(primary) : '';

  return (
    // `minWidth: 0` is the antidote to the auto-min-content overflow
    // bug: CSS Grid's `1fr` track is actually `minmax(auto, 1fr)`, so
    // a long unbreakable string in a child (a long asset filename,
    // a version like `gpt-4.1-nano-2024-07-18`) makes the column
    // expand past its 1fr share and the row overflows its container.
    // Setting `minWidth: 0` on the grid item (this Card) opts back
    // into honoring the track size.
    <Card sx={{ borderRadius: 2, height: '100%', display: 'flex', flexDirection: 'column', minWidth: 0 }}>
      <CardContent sx={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0 }}>
        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1, minWidth: 0 }}>
          <Typography variant="h6" sx={{ fontFamily: 'monospace', wordBreak: 'break-all', minWidth: 0 }}>
            v{detail.version}
          </Typography>
          <Chip
            label={detail.channel}
            size="small"
            color={channelColor(detail.channel)}
            variant="outlined"
          />
        </Stack>

        {detail.publishedAt && (
          <Typography variant="caption" color="text.secondary" sx={{ mb: 1 }}>
            Released {new Date(detail.publishedAt).toLocaleDateString()}
          </Typography>
        )}

        {detail.bodySnippet && (
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{
              mt: 1,
              mb: 2,
              whiteSpace: 'pre-line',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              maxHeight: '6em',
              // Release notes can contain long unbreakable strings (URLs,
              // long identifiers) that would otherwise push the card
              // wider than its grid track and overflow the container.
              wordBreak: 'break-word',
              overflowWrap: 'anywhere',
            }}
          >
            {detail.bodySnippet}
          </Typography>
        )}

        <Box sx={{ flex: 1 }} />

        {!hasAssets ? (
          // GitHub API call failed for this version → fallback link.
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Downloads currently unavailable —{' '}
            <Link href={detail.htmlUrl} target="_blank" rel="noopener noreferrer">
              open on GitHub
              <OpenInNewIcon sx={{ fontSize: 14, verticalAlign: 'middle', ml: 0.5 }} />
            </Link>
          </Typography>
        ) : primary ? (
          <Stack spacing={1.5} sx={{ mt: 1 }}>
            {/* Primary download — large, contained, full width. */}
            <Tooltip title={primary.name}>
              <Button
                variant="contained"
                color="primary"
                size="large"
                href={primary.downloadUrl}
                rel="noopener noreferrer"
                startIcon={<DownloadIcon />}
                sx={{ textTransform: 'none', justifyContent: 'center', py: 1.25 }}
              >
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                  <Box sx={{ fontWeight: 600 }}>
                    Download for {PLATFORM_LABEL[detected.platform]}
                  </Box>
                  <Typography variant="caption" sx={{ opacity: 0.85, fontWeight: 400, lineHeight: 1.2 }}>
                    {primaryDescriptor}
                    {primary.size > 0 ? ` · ${formatBytes(primary.size)}` : ''}
                  </Typography>
                </Box>
              </Button>
            </Tooltip>

            {/* Other operating systems — desktop apps for the other 2 platforms. */}
            {otherPlatformDesktopApps.length > 0 && (
              <Box>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                  Other operating systems
                </Typography>
                <Stack spacing={1}>
                  {otherPlatformDesktopApps.map(a => (
                    <AssetRowButton key={a.downloadUrl} asset={a} descriptor={assetDescriptor(a)} />
                  ))}
                </Stack>
              </Box>
            )}

            {/* Command-line tool — collapsed by default, for power users. */}
            {cliAssets.length > 0 && (
              <Box>
                <Button
                  size="small"
                  onClick={() => setShowAdvanced(v => !v)}
                  endIcon={showAdvanced ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                  sx={{
                    alignSelf: 'flex-start',
                    textTransform: 'none',
                    color: 'text.secondary',
                    pl: 0,
                  }}
                >
                  Command-line tool (advanced)
                </Button>
                <Collapse in={showAdvanced} unmountOnExit>
                  <Stack spacing={1} sx={{ mt: 0.5 }}>
                    {cliAssets.map(a => (
                      <AssetRowButton key={a.downloadUrl} asset={a} descriptor={assetDescriptor(a)} />
                    ))}
                  </Stack>
                </Collapse>
              </Box>
            )}

            {/* Anything we couldn't categorize — last-resort bucket. */}
            {uncategorized.length > 0 && (
              <Box>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                  Other downloads
                </Typography>
                <Stack spacing={1}>
                  {uncategorized.map(a => (
                    <AssetRowButton key={a.downloadUrl} asset={a} />
                  ))}
                </Stack>
              </Box>
            )}
          </Stack>
        ) : (
          // No primary match — either platform unknown (SSR / odd UA) or
          // no asset usable on the visitor's OS. Show everything we have,
          // organized the same way (just no big primary button).
          <Stack spacing={1.5} sx={{ mt: 1 }}>
            {detected.platform !== 'unknown' && (
              <Typography variant="caption" color="text.secondary">
                No build detected for {PLATFORM_LABEL[detected.platform]}. Other downloads:
              </Typography>
            )}
            {otherPlatformDesktopApps.length > 0 && (
              <Stack spacing={1}>
                {otherPlatformDesktopApps.map(a => (
                  <AssetRowButton key={a.downloadUrl} asset={a} descriptor={assetDescriptor(a)} />
                ))}
              </Stack>
            )}
            {cliAssets.length > 0 && (
              <Box>
                <Button
                  size="small"
                  onClick={() => setShowAdvanced(v => !v)}
                  endIcon={showAdvanced ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                  sx={{
                    alignSelf: 'flex-start',
                    textTransform: 'none',
                    color: 'text.secondary',
                    pl: 0,
                  }}
                >
                  Command-line tool (advanced)
                </Button>
                <Collapse in={showAdvanced} unmountOnExit>
                  <Stack spacing={1} sx={{ mt: 0.5 }}>
                    {cliAssets.map(a => (
                      <AssetRowButton key={a.downloadUrl} asset={a} descriptor={assetDescriptor(a)} />
                    ))}
                  </Stack>
                </Collapse>
              </Box>
            )}
            {uncategorized.length > 0 && (
              <Stack spacing={1}>
                {uncategorized.map(a => (
                  <AssetRowButton key={a.downloadUrl} asset={a} />
                ))}
              </Stack>
            )}
          </Stack>
        )}
      </CardContent>
    </Card>
  );
}

function ReleaseCardSkeleton() {
  return (
    <Card sx={{ borderRadius: 2 }}>
      <CardContent>
        <Skeleton variant="text" width="40%" height={32} />
        <Skeleton variant="text" width="80%" />
        <Skeleton variant="text" width="100%" />
        <Skeleton variant="rectangular" height={40} sx={{ mt: 2, borderRadius: 1 }} />
        <Skeleton variant="rectangular" height={40} sx={{ mt: 1, borderRadius: 1 }} />
      </CardContent>
    </Card>
  );
}

export default function CommunityDownloadsSection() {
  const [details, setDetails] = useState<CommunityReleaseDetail[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Detect once per mount. The user's OS doesn't change while they're
  // staring at the page; no need to re-run on every render.
  const detected = useMemo<DetectedEnv>(() => detectPlatform(), []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const all = await fetchAllCommunityReleaseDetails();
        if (!cancelled) setDetails(all);
      } catch (err: any) {
        if (!cancelled) setError(err?.message || 'Failed to load community releases');
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Render
  return (
    <Box sx={{ my: 6 }}>
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        alignItems={{ xs: 'flex-start', sm: 'center' }}
        spacing={1}
        sx={{ mb: 1 }}
      >
        <Typography variant="h4" component="h2" sx={{ fontWeight: 600 }}>
          OnBuzz Community
        </Typography>
        <Chip
          label="Open Source"
          color="info"
          variant="outlined"
          size="small"
          icon={<GitHubIcon sx={{ fontSize: 16 }} />}
        />
      </Stack>

      <Typography variant="body1" color="text.secondary" sx={{ mb: 3, maxWidth: 720 }}>
        The free, open-source edition. Source code lives at{' '}
        <Link
          href={`https://github.com/${GITHUB_REPO}`}
          target="_blank"
          rel="noopener noreferrer"
          underline="hover"
        >
          github.com/{GITHUB_REPO}
        </Link>
        . The downloads below come straight from GitHub Releases —
        select a build for your platform.
      </Typography>

      {/* Loading: 2 skeleton cards. */}
      {details === null && !error && (
        <Box
          sx={{
            display: 'grid',
            // `minmax(0, 1fr)` (not bare `1fr`) — see ReleaseCard for
            // the auto-min-content overflow story. Without this,
            // a long asset filename in any card pushes the entire row
            // past the Container and the layout overflows on the right.
            gridTemplateColumns: {
              xs: 'minmax(0, 1fr)',
              sm: 'repeat(2, minmax(0, 1fr))',
              md: 'repeat(3, minmax(0, 1fr))',
            },
            gap: 2,
          }}
        >
          <ReleaseCardSkeleton />
          <ReleaseCardSkeleton />
        </Box>
      )}

      {/* Error */}
      {error && (
        <Typography color="error" variant="body2">
          {error}
        </Typography>
      )}

      {/* Empty */}
      {details !== null && details.length === 0 && (
        <Box
          sx={{
            p: 4,
            border: '1px dashed',
            borderColor: 'divider',
            borderRadius: 2,
            textAlign: 'center',
          }}
        >
          <Typography variant="body1" color="text.secondary">
            No community releases listed yet.
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            See the{' '}
            <Link
              href={`https://github.com/${GITHUB_REPO}/releases`}
              target="_blank"
              rel="noopener noreferrer"
            >
              GitHub releases page
            </Link>{' '}
            for builds.
          </Typography>
        </Box>
      )}

      {/* List */}
      {details !== null && details.length > 0 && (
        <Box
          sx={{
            display: 'grid',
            // `minmax(0, 1fr)` (not bare `1fr`) — see ReleaseCard for
            // the auto-min-content overflow story. Without this,
            // a long asset filename in any card pushes the entire row
            // past the Container and the layout overflows on the right.
            gridTemplateColumns: {
              xs: 'minmax(0, 1fr)',
              sm: 'repeat(2, minmax(0, 1fr))',
              md: 'repeat(3, minmax(0, 1fr))',
            },
            gap: 2,
          }}
        >
          {details.map(d => (
            <ReleaseCard key={d.version} detail={d} detected={detected} />
          ))}
        </Box>
      )}
    </Box>
  );
}
